<template>
  <dn-skeleton
    v-if="loading"
  />
  <dn-link
    v-else
    :href="teaser.canonicalSlug"
    :class="[
      'dn-teaser-card dn-teaser-video-card', teaser.teaserPriority?.toLowerCase(),
      teaser.mobileBPlusClass?.toLowerCase(), theme?.toLowerCase(),
      teaser.imageHideClass?.toLowerCase(), teaser.imagePosition?.toLowerCase(),
      teaser.imageFormat?.toLowerCase(), teaser.mobileImagePosition.toLowerCase(),
    ]"
    :external="external"
    :data-id="id"
  >
    <dn-card
      v-track-event="trackBinding"
      :tag="tag"
      :title="teaser.title"
      :strapline="teaser.strapline"
      :lead="teaser.lead"
    >
      <template
        v-if="showVideoIcon"
        #badge
      >
        <dn-icon
          name="play"
          class="dn-play-icon"
        />
        <span v-if="durationConverter">{{ durationConverter }}</span>
      </template>
      <template
        v-if="teaser.kicker"
        #kicker
      >
        <span v-if="teaser.isOpinion">
          <dn-icon
            name="quote"
          />
        </span>
        <span>
          {{ teaser.kicker }}
        </span>
      </template>
      <template
        v-if="isVideoPreviewAvailable || teaser.leadAsset"
        #assets
      >
        <template v-if="isVideoPreviewAvailable">
          <dn-image
            v-if="!isVideoLoaded"
            :loading-mode="loadingMode"
            :fetch-priority="loadingMode === 'eager' ? 'high' : 'auto'"
            v-bind="teaser.leadAsset"
          />
          <figure
            v-show="isVideoLoaded"
            class="dn-image"
          >
            <picture :class="`ratio-${teaser.leadAsset.ratio || '16x9'}`">
              <video
                ref="videoPlayer"
                :muted="true"
                :autoplay="true"
                :loop="true"
                playsinline
                width="100%"
              >
                <source
                  v-if="showVideo"
                  :src="video?.mp4Url || teaser.videoPreviewUrl"
                  type="video/mp4"
                >
              </video>
            </picture>
          </figure>
        </template>
        <dn-image
          v-else-if="teaser.leadAsset"
          :loading-mode="loadingMode"
          :fetch-priority="loadingMode === 'eager' ? 'high' : 'auto'"
          v-bind="teaser.leadAsset"
        />
      </template>
      <template
        v-if="teaser.meta || published_at"
        #meta
      >
        <span v-if="teaser.meta">
          {{ teaser.meta }}
        </span>
        <span v-if="teaser.premium && subscriptionStatusFrontTeaser">
          {{ subscriptionStatusFrontTeaser }}
        </span>
        <dn-date-time
          v-if="published_at"
          :value="published_at"
          :format="useRuntimeConfig().public?.timeFormatLatestNews || 'DD.MM.YYYY'"
        />
      </template>
    </dn-card>
  </dn-link>
</template>
<script lang="ts" setup>
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import type { ArticlePublication } from '~/types/article';
dayjs.extend(duration);
const { t } = useI18n();
const props = defineProps({
  id: {
    type: String,
    required: true
  },
  lead_asset: {
    type: Object,
    required: false,
    default: () => {}
  },
  tag: {
    type: String,
    required: false,
    default: 'h3'
  },
  title: {
    type: String,
    required: false,
    default: null
  },
  meta: {
    type: String,
    required: false,
    default: null
  },
  strapline: {
    type: String,
    required: false,
    default: null
  },
  kicker: {
    type: String,
    required: false,
    default: null
  },
  lead: {
    type: String,
    required: false,
    default: null
  },
  hide_lead: {
    type: Boolean,
    required: false,
    default: false
  },
  priority: {
    type: String,
    required: false,
    default: null
  },
  theme: {
    type: String,
    required: false,
    default: null
  },
  image_position: {
    type: String,
    required: false,
    default: 'auto'
  },
  features: {
    type: Object,
    required: false,
    default: () => {}
  },
  prefetch: {
    type: Boolean,
    required: false,
    default: true
  },
  data_attr: {
    type: Number,
    required: false,
    default: null
  },
  assetDefaultRatio: {
    type: String,
    required: false,
    default: ''
  },
  variant: {
    type: String,
    required: false,
    default: ''
  },
  url: {
    type: String,
    required: false,
    default: ''
  },
  video: {
    type: Object,
    required: false,
    default: null
  },
  mobile_image_position: {
    type: String,
    required: false,
    default: 'auto'
  },
  image_hide: {
    type: Boolean,
    required: false,
    default: false
  },
  mobile_bplus: {
    type: Boolean,
    required: false,
    default: false
  },
  loadingMode: {
    type: String,
    required: false,
    default: 'lazy'
  },
  published_at: {
    type: String,
    required: false,
    default: null
  },
  publication: {
    type: Object as PropType<ArticlePublication>,
    required: false,
    default: () => {}
  },
  /** See DnTeaserCard.vue for the position/total rationale. */
  position: {
    type: Number,
    required: false,
    default: 0
  },
  total: {
    type: Number,
    required: false,
    default: 0
  }
});

const trackBinding = () => ({
  eventName: 'article clicked',
  component: { zone: 'article-body', element: 'dn-card' },
  attributes: {
    ...(props.position && props.total ? { element_position: `${props.position}/${props.total}` } : {}),
    title: teaser.value.title,
    canonical_slug: teaser.value.canonicalSlug,
    type: teaser.value.type,
    teaser_type: teaser.value.teaserType,
    publication: teaser.value.publication,
    theme: teaser.value.theme,
    meta: teaser.value.meta,
    kicker: teaser.value.kicker,
    zone_type: zoneType
  },
  // contract-only — sourced from data/props, not added to the teaser view-model
  content: {
    id: props.id || data.value?.id || '',
    paid: data.value?.accessLevel === 'paywall' || !!props.features?.premium,
    category: data.value?.categories?.find(category => category.default)?.title || data.value?.categories?.[0]?.title || '',
    topic: data.value?.topic?.name || ''
  }
});
const external = computed(() => {
  return !props.id?.match(/^2-\d-\d+$/) || data.value?.type === 'featureArticle';
});

/*
 * TODO: This logic duplicates the external logic, but it's specifically used
 * for the video icon. Once all articles are migrated to CPP, the external
 * logic will be removed.
 */
const showVideoIcon = computed(() => {
  return !props.id?.match(/^2-\d-\d+$/) && data.value?.type !== 'featureArticle';
});

const subscriptionStatusFrontTeaser = useRuntimeConfig().public.subscriptionStatusFrontTeaser;
const provider = inject('teaser_provider');
const assetDefaultRatio = props.assetDefaultRatio || inject('teaser_asset_default_ratio', '16x9');
const stripTitleBr = inject('strip_title_br', false);
const zoneType = inject('zone_type', ZONE_TYPE_MANUAL);
let data = ref({});
let loading = ref(false);
const isVideoLoaded = ref(false);
const isVideoPreviewAvailable = computed(() => {
  if (props.video && props.video?.showVideo && props.video?.mp4Url) {
    return true;
  } else if (teaser.value?.videoPreviewUrl) {
    return true;
  }
  return false;
});
const videoPlayer = ref<HTMLVideoElement | null>(null);
const hasFutureData = (player: HTMLVideoElement) => {
  return (player.readyState >= 3);
};
const durationConverter = computed(() => {
  if (teaser.value?.duration) {
    return dayjs.duration(teaser.value?.duration, 'seconds').format('mm:ss');
  }
  return '';
});

onMounted(() => {
  if (!isVideoPreviewAvailable.value) {
    return;
  }

  setupVideoLoading();
});

const setupVideoLoading = () => {
  if (videoPlayer.value && hasFutureData(videoPlayer.value)) {
    isVideoLoaded.value = true;
  } else {
    const stop = useEventListener(videoPlayer, 'loadeddata', () => {
      isVideoLoaded.value = true;
      stop();
    });
  }
};

const teaser = computed(() => {
  const rawTitle = props.title || data.value?.title || ' ';
  const title = stripTitleBr ? rawTitle.replace(/<br\s*\/?>/gi, ' ') : rawTitle;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { layout, caption, credit, ...leadAsset } = { ...props.lead_asset || data.value?.leadAsset };
  leadAsset.alt = title;
  leadAsset.ratio = props.features?.['image-format'] && assetDefaultRatio
    ? (props.features?.['image-format'] === 'auto' || props.features?.['image-format'] === 'fill') ? assetDefaultRatio : props.features?.['image-format']
    : assetDefaultRatio || undefined;
  leadAsset.matchAreaOfInterest = props.features?.['image-crop-mode'] === 'areaOfInterest' || false;

  const getCanonicalSlug = (data, props) => {
    const url = data.value?.canonicalSlug || props.url || `/${props.id}`;

    return props.variant ? `${url}${url.includes('?') ? '&' : '?'}abtest=${props.variant.toLowerCase()}` : url;
  };

  return {
    leadAsset,
    title,
    type: data.value?.type || '',
    canonicalSlug: getCanonicalSlug(data, props),
    lead: props.hide_lead ? null : props.lead || data.value?.intro,
    meta: props.meta || data.value?.categories?.find(category => category.default)?.title,
    kicker: props.kicker || null,
    strapline: props.strapline,
    premium: props.features?.premium,
    theme: props.theme,
    isOpinion: props.theme === 'opinion' || data.value?.metadata?.theme === 'opinion',
    teaserPriority: props.priority ? `dn-teaser-card-${props.priority}` : '',
    imageHideClass: props.image_hide ? 'dn-image-hide' : '',
    mobileBPlusClass: props.mobile_bplus ? 'dn-teaser-card-b+' : '',
    imagePosition: props.image_position ? `dn-image-${props.image_position}` : '',
    imageFormat: `dn-image-format-${props.features?.['image-format'] ? props.features?.['image-format'] : 'auto'}`,
    mobileImagePosition: `dn-image-mobile-${props.mobile_image_position ? props.mobile_image_position : 'auto'}`,
    duration: data.value?.duration,
    videoPreviewUrl: data.value?.videoPreviewUrl,
    publication: props.publication?.name || data.value?.publication?.name || ''
  };
});

const { query } = useTansQuery();

if (props.prefetch && provider && props.id) {
  try {
    const providerConfig = useRuntimeConfig()?.public?.[provider];
    const api = import.meta.client && providerConfig?.api ? providerConfig?.api : generateServerUrl(useRuntimeConfig()?.[provider]?.originApi);
    if (api) {
      const { replaceUrl } = useUrlReplace();
      const authHeader = kongApiAuthHeader(useRuntimeConfig().dnmgAuthToken as string);
      const response = query({
        api: ref(replaceUrl(api, { id: props.id })),
        options: { provider, id: props.id },
        transform: transformTeaser,
        headers: authHeader
      });
      data = response.data;
      loading = response.loading;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`Error fetching teaser ${props.id} data for ${provider || 'undefined provider'}`, e);
  }
}

watch(isVideoPreviewAvailable, (newValue) => {
  if (import.meta.client) {
    if (!newValue) {
      return;
    }
    setupVideoLoading();
  }
});

const showVideo = computed(() => {
  if (props.video && !props.video?.showVideo) {
    return false;
  }
  return !!(teaser.value?.videoPreviewUrl || props.video?.mp4Url);
});
</script>

<style lang="scss">
.dn-teaser-card {
  &.dn-teaser-video-card {
    --dn-card_badge_display: flex;
    --dn-card_assets_overlay: var(--dn-teaser-video-card--assets_overlay);
    --dn-card--badge_background: var(--dn-teaser-video-card--badge_background);
    --dn-card--badge_color: var(--dn-teaser-video-card--badge_color);
    --dn-card--badge_font-weight: var(--dn-teaser-video-card--badge_font-weight);
  }
}

.dn-play-icon {
  @media only screen and (width >= 980px) {
    height: 45px;
  }
}
</style>
